import { Injectable } from '@angular/core';

/**
 * Crops fully-transparent padding from the edges of a raster image, so the artwork stored at
 * upload is scoped to the tight bounding box of its visible (alpha > 0) pixels.
 *
 * WHY: nothing downstream distinguishes "the file" from "the artwork inside the file". Design
 * Huddle's project media and this app's own top-anchor placement (`getDesignDims` /
 * `topAnchoredMediaPosition` in product-color-selection.component.ts) both size and position a
 * logo using the image's FULL natural width/height. Many design tools export a PNG with a large
 * transparent canvas around the actual mark (e.g. a square artboard export of a small logo) — with
 * no trim, that blank margin is treated as part of the artwork, so the visible logo is scaled down
 * and offset instead of filling the print zone the way the artist intended.
 *
 * Trimming once here, before the file is uploaded, fixes placement everywhere that later reads the
 * stored URL (editor seed, preview, export) without needing any change to the placement math.
 *
 * Fails soft (decode error, fully-transparent image, no padding found) by resolving with the
 * original dataUrl unchanged — a trim failure must never block an upload.
 */
@Injectable({
  providedIn: 'root'
})
export class ImageTrimService {

  async trimTransparentPadding(dataUrl: string): Promise<string> {
    try {
      const img = await this.loadImage(dataUrl);
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      if (!width || !height) return dataUrl;

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return dataUrl;
      ctx.drawImage(img, 0, 0);

      const bounds = this.findOpaqueBounds(ctx, width, height);
      // Nothing visible (fully transparent) or already tight — no padding to remove.
      if (!bounds) return dataUrl;
      const { minX, minY, maxX, maxY } = bounds;
      if (minX === 0 && minY === 0 && maxX === width - 1 && maxY === height - 1) return dataUrl;

      const trimmedW = maxX - minX + 1;
      const trimmedH = maxY - minY + 1;
      const out = document.createElement('canvas');
      out.width = trimmedW;
      out.height = trimmedH;
      const outCtx = out.getContext('2d');
      if (!outCtx) return dataUrl;
      outCtx.drawImage(canvas, minX, minY, trimmedW, trimmedH, 0, 0, trimmedW, trimmedH);
      return out.toDataURL('image/png');
    } catch {
      return dataUrl;
    }
  }

  /** Bounding box (inclusive) of every pixel with alpha > 0, or null if none exist. */
  private findOpaqueBounds(
    ctx: CanvasRenderingContext2D, width: number, height: number
  ): { minX: number, minY: number, maxX: number, maxY: number } | null {
    const { data } = ctx.getImageData(0, 0, width, height);

    let minX = width, minY = height, maxX = -1, maxY = -1;
    for (let y = 0; y < height; y++) {
      const rowOffset = y * width;
      for (let x = 0; x < width; x++) {
        const alpha = data[(rowOffset + x) * 4 + 3];
        if (alpha > 0) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    return maxX >= minX ? { minX, minY, maxX, maxY } : null;
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Image failed to load'));
      img.src = src;
    });
  }

  /**
   * Rewrites an SVG's viewBox/width/height to the tight bounding box of its actual drawn content.
   *
   * SVGs have no pixel/alpha channel to scan — trimTransparentPadding's approach doesn't apply.
   * The equivalent "blank margin" for a vector file is a viewBox (or width/height) declared larger
   * than the geometry actually drawn inside it — e.g. a logo exported on a square artboard with the
   * mark centered in the middle third. Left alone, that's exactly the same failure mode: DH and
   * getDesignDims size/place the artwork using the declared box, not the drawn content, so the
   * visible mark ends up scaled down and offset inside the print zone.
   *
   * Measures the real geometry via SVGGraphicsElement.getBBox(), which requires the element to
   * actually be laid out — the SVG is attached off-screen (not display:none, which skips layout)
   * just long enough to measure, then removed. Rewriting viewBox to that box's origin/size crops
   * the canvas without touching any child coordinates (viewBox does the translation for free);
   * width/height are set to match so both attributes agree — resolveSvgDims() in
   * product-color-selection.component.ts reads whichever is present first.
   *
   * Fails soft (parse error, no measurable geometry, already tight within 1%) by returning the
   * original markup unchanged.
   */
  async trimSvgPadding(svgText: string): Promise<string> {
    const result = await this.trimSvgPaddingWithDims(svgText);
    return result ? result.svg : svgText;
  }

  /**
   * Same as trimSvgPadding, but also returns the trimmed artwork's dimensions (user units) so a
   * caller that is about to place the SVG into an EXISTING Design Huddle element can re-fit that
   * element's frame to the artwork's aspect ratio (see frame-fit.ts). Resolves null only when the
   * markup is unusable; "already tight" / "nothing measurable" still resolve with the original
   * markup plus its declared box when one exists.
   */
  async trimSvgPaddingWithDims(svgText: string): Promise<{ svg: string; width: number; height: number } | null> {
    try {
      const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
      if (doc.querySelector('parsererror')) return null;

      const svg = doc.documentElement as unknown as SVGSVGElement;
      if (!svg || svg.nodeName.toLowerCase() !== 'svg') return null;

      const declared = this.getDeclaredBox(svg);
      const bbox = this.measureContentBBox(svg);
      if (!bbox) {
        return declared ? { svg: svgText, width: declared.width, height: declared.height } : null;
      }

      if (declared && this.isAlreadyTight(declared, bbox)) {
        return { svg: svgText, width: declared.width, height: declared.height };
      }

      svg.setAttribute('viewBox', `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`);
      svg.setAttribute('width', String(bbox.width));
      svg.setAttribute('height', String(bbox.height));

      return { svg: new XMLSerializer().serializeToString(svg), width: bbox.width, height: bbox.height };
    } catch {
      return null;
    }
  }

  /** Natural pixel size of a raster data URL (or any loadable image URL); null when it cannot load. */
  async getDataUrlDims(dataUrl: string): Promise<{ width: number; height: number } | null> {
    try {
      const img = await this.loadImage(dataUrl);
      if (!img.naturalWidth || !img.naturalHeight) return null;
      return { width: img.naturalWidth, height: img.naturalHeight };
    } catch {
      return null;
    }
  }

  /** Bounding box of the SVG's actual drawn content, in its own user-unit coordinate space. */
  private measureContentBBox(svg: SVGSVGElement): { x: number, y: number, width: number, height: number } | null {
    const host = document.createElement('div');
    // position:fixed + off-screen keeps this out of the visible page and out of document flow,
    // without display:none — display:none elements receive no layout, and getBBox() throws or
    // returns a zero rect on an element that was never laid out.
    host.style.position = 'fixed';
    host.style.top = '-99999px';
    host.style.left = '-99999px';
    host.style.width = '0';
    host.style.height = '0';
    host.style.overflow = 'hidden';
    document.body.appendChild(host);

    try {
      // The parsed SVG belongs to a document created by DOMParser — import it into the live
      // document before appending.
      const imported = document.importNode(svg, true) as unknown as SVGGraphicsElement;
      host.appendChild(imported as unknown as Node);

      const box = imported.getBBox();
      return box.width > 0 && box.height > 0 ? { x: box.x, y: box.y, width: box.width, height: box.height } : null;
    } catch {
      return null;
    } finally {
      document.body.removeChild(host);
    }
  }

  /** The SVG's currently-declared box: explicit width/height if present, else the viewBox. */
  private getDeclaredBox(svg: SVGSVGElement): { width: number, height: number } | null {
    const width = this.parseSvgLength(svg.getAttribute('width'));
    const height = this.parseSvgLength(svg.getAttribute('height'));
    if (width > 0 && height > 0) return { width, height };

    const viewBox = (svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
    if (viewBox.length === 4 && viewBox[2] > 0 && viewBox[3] > 0) {
      return { width: viewBox[2], height: viewBox[3] };
    }
    return null;
  }

  /** Numeric SVG length in user units. Returns 0 for percentages or units this can't resolve. */
  private parseSvgLength(value: string | null): number {
    if (!value) return 0;
    const match = /^\s*([0-9]*\.?[0-9]+)\s*(px)?\s*$/i.exec(value);
    return match ? Number(match[1]) : 0;
  }

  /** True when the measured content already fills its declared box to within 1% on both axes. */
  private isAlreadyTight(
    declared: { width: number, height: number }, bbox: { width: number, height: number }
  ): boolean {
    const widthTight = Math.abs(declared.width - bbox.width) <= declared.width * 0.01;
    const heightTight = Math.abs(declared.height - bbox.height) <= declared.height * 0.01;
    return widthTight && heightTight;
  }
}
